import { Address } from '@vsolv/core/address/domain';
import moment from 'moment';
import { PropertySet } from '.';
import { PropertyType } from '../property/models';
import { PropertyAssignment } from './models';

export const format = (
  propertySet: Pick<PropertySet.Model, 'id' | 'properties'>,
  values: Record<string, unknown>
): { id: string; label: string; value: string | null }[] => {
  const fields: { id: string; label: string; value: string | null }[] = [];
  _format(propertySet, values, fields);
  return fields;
};

const _format = (
  propertySet: { id: string; properties?: PropertyAssignment[] },
  values: Record<string, unknown>,
  fields: { id: string; label: string; value: string | null }[],
  preName?: string
) => {
  propertySet.properties
    ?.sort((p0, p1) => p0.order - p1.order)
    ?.forEach(property => {
      const value = values[property.property?.valueKey || property.propertyId];

      // In case a formId is provided, we will try to format the value
      if (value && property.property?.formId) {
        const formattedValue = _formatPropertyFormId(property.property.formId, value);
        if (formattedValue) {
          fields.push({
            id: property.propertyId,
            label: (preName ? preName + ' ' : '') + (property.property?.name || property.propertyId),
            value: formattedValue + '',
          });
          return;
        }
      }

      // In case the formId failed, we will try to format the object
      if (value && property.property?.type === PropertyType.OBJECT) {
        _format(
          property.property,
          value as Record<string, unknown>,
          fields,
          property.property?.name === 'Road Vehicle' ? undefined : property.property?.name || property.propertyId
        );
        return;
      }

      // In case the property is not an object, we will try to format the value
      const formattedValue = property.property?.type
        ? _formatProperty(property.property?.type, value, property.property?.config?.format)
        : value;
      if (formattedValue) {
        fields.push({
          id: property.propertyId,
          label: (preName || '') + (property.property?.name || property.propertyId),
          value: formattedValue + '',
        });
      }
    });
};

const _formatPropertyFormId = (formId: string, value: unknown): string | null => {
  switch (formId) {
    case 'address': {
      const address = value as Address.Model;
      return `${address.line1}${address.line2 ? ',' + address.line2 : ''}<br/>
              ${address.city} ${address.state} ${address.zip}<br/>
              ${address.country}`;
    }
    case 'home_inspection': {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const inspection = value as any;
      return `Inspected on ${moment(inspection.timestamp).format('LL')}<br/>
              by ${inspection.inspector.name} ${
                inspection.inspector.companyName ? '(' + inspection.inspector.companyName + ')' : ''
              }<br/>
      ${inspection.inspector.email ? inspection.inspector.email + '<br/>' : ''}
      ${inspection.inspector.phone ? inspection.inspector.phone : ''}
      `;
    }
    default:
      return null;
  }
};

const _formatProperty = (type: PropertyType, value: unknown, format?: string | null): string => {
  switch (type) {
    case PropertyType.BOOLEAN:
      return value ? 'Yes' : 'No';
    case PropertyType.DATE:
      switch (format) {
        case 'date':
          return moment(value as string)?.format('LL') || '';
        case 'datetime':
          return moment(value as string)?.format('LLL') || '';
        default:
          return moment(value as string)?.format('LL') || '';
      }
    case PropertyType.NUMBER:
      switch (format) {
        case 'currency':
          return ((value as number) / 100)?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) || '';
        case 'percent':
          return (value as number)?.toLocaleString('en-US', { style: 'percent' }) || '';
        default:
          return value ? value + '' : '';
      }
    case PropertyType.TEXT:
      switch (format) {
        case 'phone': {
          return value ? value + '' : '';
        }
        case 'email':
          return (value as string)?.toLowerCase() || '';
        default:
          return value ? value + '' : '';
      }
    default:
      return value ? value + '' : '';
  }
};
